/* eslint-disable */
import { of } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { catchError, map, retry } from 'rxjs/operators';
import envGet from '../../Support/Env/Get/EnvGet.support';
import './User.service.i18n';

const baseUrl = envGet({ key: `REACT_APP_BORAL_BASE_URL` });
const BASE_URL_FLITCH = envGet({ key: `REACT_APP_BORAL_BASE_URL_FLITCH` });

const userAccountsUrl = envGet({ key: `REACT_APP_BORAL_USER_ACCOUNTS_URL` });
const updateUserUrl = envGet({ key: `REACT_APP_BORAL_UPDATE_USER_URL` });
const userDetailsUrl = envGet({ key: `REACT_APP_BORAL_USER_DETAILS_URL` });
const userSettingsUrl = envGet({ key: `REACT_APP_BORAL_USER_SETTINGS_URL` });
const ACCOUNT_REACTIVATE_URL = envGet({ key: `REACT_APP_BORAL_ACCOUNT_REACTIVATE_URL` });

/**
 * UserService
 * @class UserService
 */
class UserService {
  /**
   * Creates an instance of `UserService`
   * @param {UserServiceCreateDto} dto The params required to create an instance of `UserService`
   * @memberof UserService
   */
  constructor({ authService, httpService }) {
    this.httpService = httpService;
    this.authService = authService;
  }

  /**
   * Find a user by their `id`
   * @param {UserServiceFindByIdDto} dto The required params to find a `user` by their `id`
   * @returns {Promise<UserEntity>} The found `user`
   * @memberof UserService
   */
  findById = async ({ id }) => {
    const profile = await this.authService.getUser(id);
    return profile;
  };

  /**
   * Get the current user
   * @returns {Promise<UserEntity>} The found `user`
   * @memberof UserService
   */
  getCurrentUser = async () => {
    const userInfo = await this.authService.getUser();
    return userInfo;
  };

  /**
   * Updates user association with billTo
   * @param {string} userId user's email
   * @param {string} userType user type (role)
   * @param {string} billToAccount billTo number
   * @returns {Promise<Array>} The result
   * @memberof UserService
   */
  updateUserType = async (userId, userType, billToAccount, accountName) => {
    const headers = this.authService.createHeaders();
    const url = `${baseUrl}${updateUserUrl}/${billToAccount}:super`;
    const data = {
      billToAccount: userType ? `${billToAccount}:${userType}` : billToAccount,
      accountName,
      email: userId,
    };
    return this.httpService.put(url, { data, headers });
  };

  /**
   * Updates user association with billTo
   * @param {string} userId user's email
   * @param {string} userType user type (role)
   * @param {string} billToAccount billTo number
   * @returns {Promise<Array>} The result
   * @memberof UserService
   */
  inviteUser = async (userId, userType, billToAccount, accountName) => {
    const headers = this.authService.createHeaders();
    const url = `${baseUrl}${updateUserUrl}/${billToAccount}:super`;
    const data = {
      billToAccount: `${billToAccount}:${userType.toLowerCase()}`,
      accountName,
      email: userId,
    };

    return this.httpService.post(url, { data, headers }).catch((ex) => {
      // get message only
      throw new Error(ex.response.data.message);
    });
  };

  /**
   * Get current user accounts
   * @returns {Promise<Array>} The orders
   * @memberof UserService
   */
  getUserAccounts = async () => {
    const headers = this.authService.createHeaders();
    const url = `${baseUrl}${userAccountsUrl}`;
    try {
      const accounts = await this.httpService.get(url, { headers, params: {} });
      return accounts;
    } catch (ex) {
      return [];
    }
  };

  /**
   * Get current user account by billTo number
   * @param {string} billTo the billTo number
   * @returns {Promise<Object | null>} The account information
   * @memberof UserService
   */
  getUserAccount = async (billTo) => {
    const headers = this.authService.createHeaders();
    const url = `${baseUrl}${userAccountsUrl}/${billTo}`;
    try {
      const account = await this.httpService.get(url, { headers, params: {} });
      return account;
    } catch (ex) {
      return null;
    }
  };

  /**
   * Updates the user profile
   * @param {Object} profile The profile object
   * @returns {Promise<UserEntity>} The found `user`
   * @memberof UserService
   */
  updateUserProfile = async (profile) => {
    const userInfo = await this.authService.updateUserProfile(profile);
    return userInfo;
  };

  /**
   * Gets user details such as if user has verified its mobile number, was informed about SMS functionality being enabled etc. This is outside Okta
   * @param {number} howMany how many notifications to retrieve
   * @returns {Observable} Obeservable
   */
  getUserDetails = () => {
    const headers = this.authService.createHeaders();
    return ajax({
      headers,
      url: `${baseUrl}${userDetailsUrl}`,
    })
      .pipe(
        map((e) => e.response),
        catchError((error) =>
          of({
            notifiedAboutSms: false,
            confirmedMobile: false,
          }),
        ),
        // map((e) => e.response),
        // catchError((error) => of(error)),
      )
      .toPromise();
  };

  /**
   * savees user settings e.g. flags for user been notified about SMS available now
   * @param {Object} settings object to be send to backend
   * @returns {Promise} promise
   */
  saveSettings = async (settings) => {
    const headers = this.authService.createHeaders();
    const body = {
      ...settings,
    };

    return ajax({
      body,
      headers: {
        ...headers,
        'Content-Type': `application/json`,
      },
      method: `PUT`,
      url: `${baseUrl}${userSettingsUrl}`,
    })
      .pipe(retry(1))
      .toPromise();
  };

  reactivate = async (userAccount) => {
    const headers = this.authService.createHeaders();
    const body = {
      email: userAccount.email,
    }
    return ajax({
      body,
      headers: {
        ...headers,
        'Content-Type': `application/json`,
      },
      method: `POST`,
      url: `${BASE_URL_FLITCH}${ACCOUNT_REACTIVATE_URL}`,
    })
      .pipe(retry(1))
      .toPromise();
  }
}

export default UserService;
